import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from '../../app/baseQueryWithIam'
import { CourseWithPricingState, SubjectState, BasketState } from '../types'
import { paymentApi } from '../payment/payment'

// ---------------- Типы ответов ----------------
export interface CourseSubjectResponse {
    id: number
    subject: SubjectState
    languages: string[]
    topicCount: number
    isRequired: boolean
    description: string
    authorId: number
    image: string
    curatorIds: number[]
}

export interface CourseSalesParams {
    courseType: string
    subjectId?: number
    subjectIds?: number[]
    promocode?: string
}



export const courseSalesApi = createApi({
    reducerPath: 'courseSalesApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Subject', 'Basket'],
    endpoints: (build) => ({

        // Получение предметов по типу курса
        fetchSubjectsByCourseType: build.query<CourseSubjectResponse[], CourseSalesParams>({
            query: (params) => ({
                url: `landing-course/subject?courseType=${params.courseType}`,
            }),
            transformResponse: (response: { data?: CourseSubjectResponse[] }) => response?.data ?? [],
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Subject' as const, id })),
                        { type: 'Subject', id: 'LIST' },
                    ]
                    : [{ type: 'Subject', id: 'LIST' }],
        }),

        // Получение цен курсов по предметам
        fetchCoursesPriceBySubjectIds: build.query<CourseWithPricingState[], CourseSalesParams>({
            query: (params) => {
                const queryParts = [
                    `courseType=${params.courseType}`,
                    params.subjectIds?.length ? `subjectIds=${params.subjectIds.join(',')}` : '',
                ].filter(Boolean)

                return {
                    url: `landing-course/course?${queryParts.join('&')}`,
                }
            },
            transformResponse: (response: { data?: CourseWithPricingState[] }) => response?.data ?? [],
        }),

        // Получение корзины
        fetchBasketPrice: build.query<
            BasketState,
            { courseId: number; subjectIds: number[]; promocode?: string }
        >({
            query: (params) => {
                const queryParts = [
                    `courseId=${params.courseId}`,
                    params.subjectIds?.length ? `subjectIds=${params.subjectIds.join(',')}` : '',
                    params.promocode ? `promocode=${encodeURIComponent(params.promocode)}` : '',
                ].filter(Boolean)

                return {
                    url: `landing-course/basket?${queryParts.join('&')}`,
                }
            },
            transformResponse: (response: { data?: BasketState }) =>
                response?.data && typeof response.data === 'object'
                    ? response.data
                    : { Course: [], CourseSubject: [] },
            providesTags: ['Basket'],
        }),

        // Покупка курса
        buyCourse: build.mutation<
            any,
            {
                courseId: number
                subjectIds: number[]
                isMonthly: boolean
                firstName: string
                lastName: string
                mobile: string
                language: string
                address?: string
                promocode?: string
            }
        >({
            query: (body) => ({
                url: `landing-course/buy`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Basket'],
        }),

        // Применение промокода
        applyPromocode: build.query<CourseWithPricingState[], CourseSalesParams>({
            query: (params) => {
                const queryParts = [
                    `courseType=${params.courseType}`,
                    params.subjectIds?.length ? `subjectIds=${params.subjectIds.join(',')}` : '',
                    params.promocode ? `promocode=${encodeURIComponent(params.promocode)}` : '',
                ].filter(Boolean)

                return {
                    url: `landing-course/course?${queryParts.join('&')}`,
                }
            },
            transformResponse: (response: { data?: CourseWithPricingState[] }) => response?.data ?? [],
        }),
    }),
})

export const paymentKaspiOnlineApi = createApi({
    reducerPath: 'paymentKaspiOnlineApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://kaspi.kz',
        prepareHeaders: (headers) => {
            headers.set('Content-Type', 'application/json');
            headers.set('Accept', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['PaymentKaspiOnline'],
    endpoints: (build) => ({
        initKaspiPayment: build.mutation<
            { code: number; redirectUrl?: string; message?: string; qrCodeImage?: string },
            { TranId: string; OrderId: string; Amount: number; Service: string; returnUrl: string; refererHost: string; GenerateQrCode?: boolean }
        >({
            query: (body) => ({
                url: `online`,
                method: 'POST',
                body,
            }),
        }),
    }),
})